import React, { useEffect, useRef, useState } from "react";

/**
 * MyRichEditor.jsx — React + Tailwind custom rich-text editor (no external libs)
 * - Toolbar: Paragraph/H1–H6, Font family (incl. Cambria), Font size, B/I/U/Strike,
 *   Sub/Superscript, Alignments, Lists, Indent/Outdent, Text/Highlight color,
 *   Link/Unlink, Image Upload (POST -> JSON {url}), Insert Table, HTML toggle, Save.
 * - Shortcuts: Ctrl+B / Ctrl+I / Ctrl+U / Ctrl+S
 * - Props:
 *    initialHtml: string
 *    onChange(html): fn
 *    onSave(html): fn
 *    uploadUrl: string (default: "/api/upload.php")
 */

export default function MyRichEditor({
  initialHtml = "",
  onChange,
  onSave,
  uploadUrl = "/api/upload.php",
}) {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);

  const [html, setHtml] = useState(initialHtml);
  const [showHtml, setShowHtml] = useState(false);
  const [font, setFont] = useState("Tahoma");
  const [fontSize, setFontSize] = useState("3"); // HTML sizes 1–7
  const [busy, setBusy] = useState(false);

  useEffect(() => setHtml(initialHtml), [initialHtml]);

  // ---- helpers -------------------------------------------------------------
  const sync = () => {
    const current = editorRef.current?.innerHTML ?? "";
    setHtml(current);
    onChange && onChange(current);
  };

  const exec = (cmd, val = null) => {
    document.execCommand(cmd, false, val);
    sync();
    editorRef.current?.focus();
  };

  const applyBlock = (block) => exec("formatBlock", block);
  const handleInput = () => sync();

  const handlePickImage = () => fileInputRef.current?.click();

  // Robust upload: tolerates HTML error responses and shows the real message
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setBusy(true);
      const fd = new FormData();
      fd.append("image", file);

      const res = await fetch(uploadUrl, {
        method: "POST",
        headers: { Accept: "application/json" }, // ask for JSON
        body: fd,
      });

      const text = await res.text(); // read raw
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        throw new Error(
          `Server did not return JSON (status ${res.status}). ` +
            `Response starts with: ${text.slice(0, 200)}`
        );
      }

      if (!res.ok || data.success === false) {
        throw new Error(data.message || `Upload failed (status ${res.status})`);
      }

      const url = data.url || data.data?.url;
      if (!url) throw new Error("No URL in JSON response.");

      document.execCommand("insertImage", false, url);
      sync();
    } catch (err) {
      alert("Upload error: " + err.message);
      console.error(err);
    } finally {
      setBusy(false);
      e.target.value = ""; // reset chooser
    }
  };

  // Shortcuts
  useEffect(() => {
    const handler = (e) => {
      if (!e.ctrlKey && !e.metaKey) return;
      const k = e.key.toLowerCase();
      if (["b", "i", "u", "s"].includes(k)) e.preventDefault();
      if (k === "b") exec("bold");
      if (k === "i") exec("italic");
      if (k === "u") exec("underline");
      if (k === "s") onSave && onSave(html);
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [html, onSave]);

  // Apply chosen font & size to future typing
  useEffect(() => {
    document.execCommand("fontName", false, font);
    document.execCommand("fontSize", false, fontSize);
  }, [font, fontSize]);

  return (
    <div className="min-h-screen bg-white p-0 dark:bg-slate-950">
      <div className="mx-auto max-w-6xl">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center gap-1 border border-slate-200 bg-white p-2 dark:border-slate-800 dark:bg-slate-900">
          {/* Paragraph / Headings */}
          <select
            className="rounded border border-slate-300 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800"
            onChange={(e) => applyBlock(e.target.value)}
            defaultValue="<p>"
            title="Paragraph"
          >
            <option value="<p>">Paragraph</option>
            <option value="<h1>">Heading 1</option>
            <option value="<h2>">Heading 2</option>
            <option value="<h3>">Heading 3</option>
            <option value="<h4>">Heading 4</option>
            <option value="<h5>">Heading 5</option>
            <option value="<h6>">Heading 6</option>
          </select>

          {/* Font family (incl. Cambria) */}
          <select
            value={font}
            onChange={(e) => setFont(e.target.value)}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800"
            title="Font"
          >
            {[
              "Tahoma",
              "Verdana",
              "Arial",
              "Georgia",
              "Times New Roman",
              "Courier New",
              "Cambria",
            ].map((f) => (
              <option key={f} value={f}>
                {f}
              </option>
            ))}
          </select>

          {/* Font size */}
          <select
            value={fontSize}
            onChange={(e) => setFontSize(e.target.value)}
            className="rounded border border-slate-300 bg-white px-2 py-1 text-xs dark:border-slate-700 dark:bg-slate-800"
            title="Font size"
          >
            {[1, 2, 3, 4, 5, 6, 7].map((s) => (
              <option key={s} value={s}>
                {s + 8}pt
              </option>
            ))}
          </select>

          <Divider />

          {/* Inline styles */}
          <Group>
            <Btn onClick={() => exec("bold")} title="Bold">
              <b>B</b>
            </Btn>
            <Btn onClick={() => exec("italic")} title="Italic">
              <i>I</i>
            </Btn>
            <Btn onClick={() => exec("underline")} title="Underline">
              <u>U</u>
            </Btn>
            <Btn onClick={() => exec("strikeThrough")} title="Strike">
              S
            </Btn>
            <Btn onClick={() => exec("subscript")} title="Subscript">
              x<sub>2</sub>
            </Btn>
            <Btn onClick={() => exec("superscript")} title="Superscript">
              x<sup>2</sup>
            </Btn>
          </Group>

          <Divider />

          {/* Alignment */}
          <Group>
            <Btn onClick={() => exec("justifyLeft")} title="Align left">
              ≡
            </Btn>
            <Btn onClick={() => exec("justifyCenter")} title="Align center">
              ≣
            </Btn>
            <Btn onClick={() => exec("justifyRight")} title="Align right">
              ≡
            </Btn>
            <Btn onClick={() => exec("justifyFull")} title="Justify">
              ▤
            </Btn>
          </Group>

          <Divider />

          {/* Lists & indent */}
          <Group>
            <Btn onClick={() => exec("insertUnorderedList")} title="Bulleted list">
              •
            </Btn>
            <Btn onClick={() => exec("insertOrderedList")} title="Numbered list">
              1.
            </Btn>
            <Btn onClick={() => exec("outdent")} title="Outdent">
              ←
            </Btn>
            <Btn onClick={() => exec("indent")} title="Indent">
              →
            </Btn>
          </Group>

          <Divider />

          {/* Colors */}
          <Group>
            <input
              type="color"
              title="Text color"
              onChange={(e) => exec("foreColor", e.target.value)}
            />
            <input
              type="color"
              title="Highlight"
              onChange={(e) => exec("hiliteColor", e.target.value)}
            />
          </Group>

          <Divider />

          {/* Links */}
          <Group>
            <Btn
              onClick={() => exec("createLink", prompt("URL", "https://"))}
              title="Insert link"
            >
              🔗
            </Btn>
            <Btn onClick={() => exec("unlink")} title="Remove link">
              ⛔
            </Btn>
          </Group>

          <Divider />

          {/* Image upload */}
          <Group>
            <Btn onClick={handlePickImage} title="Insert image">
              🖼️
            </Btn>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </Group>

          <Divider />

          {/* Insert Table */}
          <Group>
            <Btn
              title="Insert table"
              onClick={() => {
                const rows = parseInt(prompt("Rows?", "2"), 10) || 2;
                const cols = parseInt(prompt("Columns?", "2"), 10) || 2;
                let table =
                  "<table border='1' style='border-collapse:collapse;width:100%;'>";
                for (let r = 0; r < rows; r++) {
                  table += "<tr>";
                  for (let c = 0; c < cols; c++) {
                    table += "<td style='padding:6px'>Cell</td>";
                  }
                  table += "</tr>";
                }
                table += "</table><br/>";
                document.execCommand("insertHTML", false, table);
                sync();
              }}
            >
              ▦ Table
            </Btn>
          </Group>

          <div className="ml-auto flex items-center gap-2">
            <Btn onClick={() => setShowHtml((s) => !s)} title="View HTML">
              {showHtml ? "Preview" : "HTML"}
            </Btn>
            <Btn onClick={() => onSave && onSave(html)} title="Save">
              💾
            </Btn>
          </div>
        </div>

        {/* Editor / HTML */}
        {showHtml ? (
          <textarea
            className="h-[420px] w-full border border-slate-200 p-3 font-mono text-sm text-left dark:border-slate-800" // CHANGED
            dir="ltr" // CHANGED
            value={html}
            onChange={(e) => setHtml(e.target.value)}
            onBlur={() => {
              if (editorRef.current) editorRef.current.innerHTML = html;
              onChange && onChange(html);
            }}
            style={{ unicodeBidi: "plaintext" }} // CHANGED
          />
        ) : (
          <div
            ref={editorRef}
            className="min-h-[480px] w-full border border-slate-200 p-4 leading-7 text-left outline-none focus:ring-2 focus:ring-emerald-500 dark:border-slate-800" // CHANGED
            dir="ltr" // CHANGED
            contentEditable
            suppressContentEditableWarning
            onInput={handleInput}
            style={{ fontFamily: font, unicodeBidi: "plaintext" }} // CHANGED
            dangerouslySetInnerHTML={{ __html: html || "<p>Start writing…</p>" }}
          />
        )}

        {busy && <div className="mt-2 text-sm text-slate-500">Uploading image…</div>}
      </div>
    </div>
  );
}

/* ---------------- UI atoms ---------------- */
function Btn({ onClick, children, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium hover:bg-slate-100 active:scale-[.98] dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700"
    >
      {children}
    </button>
  );
}

function Group({ children }) {
  return <div className="flex items-center gap-1">{children}</div>;
}

function Divider() {
  return <div className="mx-1 h-6 w-px bg-slate-200 dark:bg-slate-800" />;
}
